import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send, ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

interface Issue {
  id: string;
  title: string;
  description: string;
  issue_type: string;
  status: string;
  created_at: string;
  profile_id: string;
  profiles?: { full_name: string | null } | null;
}

interface Comment {
  id: string;
  content: string;
  created_at: string;
  profile_id: string;
  profiles?: { full_name: string | null } | null;
}

interface IssueDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issue: Issue | null;
  onBack?: () => void;
  /** Allow status changes (admin/superadmin) */
  canManage?: boolean;
  onStatusChange?: (issueId: string, status: string) => void;
}

const statusColor: Record<string, string> = {
  open: "bg-blue-100 text-blue-800 border-blue-200",
  closed: "bg-gray-100 text-gray-600 border-gray-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};

const typeLabel: Record<string, string> = {
  bug: "Bug",
  feature_request: "Feature Request",
  data_syncing: "Data Syncing",
  custom_help: "Custom Help",
  other: "Other",
};

const IssueDetailDialog = ({ open, onOpenChange, issue, onBack, canManage, onStatusChange }: IssueDetailDialogProps) => {
  const { profile } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!issue || !open) return;
    const fetchComments = async () => {
      setLoadingComments(true);
      const { data } = await supabase
        .from("issue_comments" as any)
        .select("id, content, created_at, profile_id, profiles(full_name)")
        .eq("issue_id", issue.id)
        .order("created_at", { ascending: true });
      setComments((data as any[]) || []);
      setLoadingComments(false);
    };
    fetchComments();
  }, [issue, open]);

  const handleAddComment = async () => {
    if (!newComment.trim() || !issue || !profile) return;
    setSubmitting(true);
    const { data, error } = await supabase
      .from("issue_comments" as any)
      .insert({
        issue_id: issue.id,
        profile_id: profile.id,
        content: newComment.trim(),
      } as any)
      .select("id, content, created_at, profile_id, profiles(full_name)")
      .single();
    if (error) {
      toast.error("Failed to add comment");
    } else {
      setComments((prev) => [...prev, data as any]);
      setNewComment("");
    }
    setSubmitting(false);
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!issue) return;
    await supabase.from("issues" as any).update({ status: newStatus, updated_at: new Date().toISOString() } as any).eq("id", issue.id);
    onStatusChange?.(issue.id, newStatus);
    toast.success(`Issue marked as ${newStatus}`);
  };

  if (!issue) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {onBack && (
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onBack}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <DialogTitle className="text-lg leading-tight">{issue.title}</DialogTitle>
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <Badge variant="outline" className="text-xs">{typeLabel[issue.issue_type] || issue.issue_type}</Badge>
            <Badge variant="outline" className={`text-xs ${statusColor[issue.status] || ""}`}>{issue.status}</Badge>
            <span className="text-xs text-muted-foreground">
              by {issue.profiles?.full_name || "Unknown"} on {format(new Date(issue.created_at), "MMM d, yyyy")}
            </span>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 mt-2">
          {/* Description */}
          <div className="rounded-md border border-border p-3">
            <div
              className="prose prose-sm max-w-none text-foreground [&_img]:max-w-full [&_img]:rounded-md"
              dangerouslySetInnerHTML={{ __html: issue.description || "<p class='text-muted-foreground'>No description provided.</p>" }}
            />
          </div>

          {/* Status actions for admins */}
          {canManage && issue.status === "open" && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Change status:</span>
              <Button size="sm" variant="outline" onClick={() => handleStatusChange("closed")}>
                Close
              </Button>
              <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleStatusChange("rejected")}>
                Reject
              </Button>
            </div>
          )}
          {canManage && issue.status !== "open" && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Change status:</span>
              <Button size="sm" variant="outline" onClick={() => handleStatusChange("open")}>
                Reopen
              </Button>
            </div>
          )}

          {/* Comments */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Comments ({comments.length})</h3>
            {loadingComments ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : comments.length === 0 ? (
              <p className="text-sm text-muted-foreground">No comments yet.</p>
            ) : (
              <div className="space-y-3">
                {comments.map((c) => (
                  <div key={c.id} className="rounded-md border border-border p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium">{c.profiles?.full_name || "Unknown"}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {format(new Date(c.created_at), "MMM d, yyyy h:mm a")}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{c.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add comment */}
          <div className="flex gap-2">
            <Textarea
              placeholder="Write a comment…"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="min-h-[60px] flex-1"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleAddComment();
                }
              }}
            />
            <Button
              size="icon"
              className="shrink-0 self-end"
              disabled={!newComment.trim() || submitting}
              onClick={handleAddComment}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IssueDetailDialog;
